import type { CSSProperties, JSX } from 'react'

import { BufferIcon, ChannelIcon, channelColors } from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import styles from './OneWayLikeIllustration.module.css'

type OneWayLikeIllustrationProps = {
  /** Source network, shown on the right card. */
  service: ChannelType
}

// The network card's background is driven by a CSS custom property, which
// `CSSProperties` doesn't type on its own.
type ChannelCardStyle = CSSProperties & { '--channel-color': string }

// The connecting arrows' green ramp, matching the Figma illustration exactly.
// Kept as literal SVG fills (not CSS tokens) so the graphic renders identically
// to the design regardless of theme — like the other coachmark illustrations.
// (The Buffer mark itself is a themed `BufferIcon`; its fixed mint-on-dark-tile
// colours live in the stylesheet.)
// Exception: the crossed "doesn't sync back" mask + X are theme-aware in CSS
// (`.crossMask` / `.cross`) so the disc tracks the coachmark surface and the X
// stays legible in dark mode; GRAY_CROSS is only the light-mode default.
const GREEN_FORWARD = '#90d788'
const GREEN_CHECK = '#5ba71a'
const GREEN_HALO = '#d9f1d1'
const GRAY_DASH = '#bcbab6'
const GRAY_CROSS = '#8c8b88'

/**
 * The one-way-sync graphic for the first-time like coachmark: Buffer on the
 * left, the source network on the right, a green ticked arrow flowing right
 * (Buffer → network works) and a dashed, crossed-out arrow flowing back
 * (network → Buffer doesn't). Mirrors the Figma "_popover content" illustration.
 */
export const OneWayLikeIllustration = ({
  service,
}: OneWayLikeIllustrationProps): JSX.Element => {
  const channelCardStyle: ChannelCardStyle = {
    '--channel-color': channelColors[service],
  }

  return (
    <div className={styles.illustration} aria-hidden="true">
      <div className={`${styles.card} ${styles.bufferTile}`}>
        <BufferIcon className={styles.bufferIcon} size={32} />
      </div>

      <svg
        className={styles.arrows}
        viewBox="0 0 92 58"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Buffer → network: works */}
        <line
          x1="2"
          y1="18"
          x2="85"
          y2="18"
          stroke={GREEN_FORWARD}
          strokeWidth="1.5"
        />
        <path
          d="M85 14 L90 18 L85 22"
          stroke={GREEN_FORWARD}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="46" cy="18" r="16" fill={GREEN_HALO} />
        <circle cx="46" cy="18" r="10" fill={GREEN_CHECK} />
        <path
          d="M41.5 18 L44.5 21 L50.5 15"
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* network → Buffer: doesn't sync back */}
        <line
          x1="7"
          y1="46"
          x2="90"
          y2="46"
          stroke={GRAY_DASH}
          strokeWidth="1.5"
          strokeDasharray="4 4"
        />
        <path
          d="M7 42 L2 46 L7 50"
          stroke={GRAY_DASH}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="46" cy="46" r="10" className={styles.crossMask} />
        <path
          className={styles.cross}
          d="M42.5 42.5 L49.5 49.5 M49.5 42.5 L42.5 49.5"
          stroke={GRAY_CROSS}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>

      <div className={styles.card} style={channelCardStyle}>
        <span className={styles.channelBadge}>
          <ChannelIcon type={service} size="small" />
        </span>
      </div>
    </div>
  )
}
